"use client";

import { useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Fade,
  Zoom,
  TextField,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Code,
  Close,
  CheckCircle,
  DataObject,
  ContentCopy,
  Download,
} from "@mui/icons-material";

interface JsonFile {
  file: File;
  id: string;
  content?: string;
}

function jsonToXml(
  obj: unknown,
  rootName: string = "root",
  indent: string = ""
): string {
  const newIndent = indent + "  ";

  if (obj === null || obj === undefined) {
    return `${indent}<${rootName}></${rootName}>`;
  }

  if (
    typeof obj === "string" ||
    typeof obj === "number" ||
    typeof obj === "boolean"
  ) {
    const escapedValue = String(obj)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
    return `${indent}<${rootName}>${escapedValue}</${rootName}>`;
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      return `${indent}<${rootName}></${rootName}>`;
    }
    const itemName = rootName.endsWith("s") ? rootName.slice(0, -1) : "item";
    const items = obj
      .map((item) => jsonToXml(item, itemName, newIndent))
      .join("\n");
    return `${indent}<${rootName}>\n${items}\n${indent}</${rootName}>`;
  }

  if (typeof obj === "object") {
    const entries = Object.entries(obj as Record<string, unknown>);
    if (entries.length === 0) {
      return `${indent}<${rootName}></${rootName}>`;
    }
    const children = entries
      .map(([key, value]) => {
        // Sanitize key to be a valid XML tag name
        const safeKey = key
          .replace(/[^a-zA-Z0-9_-]/g, "_")
          .replace(/^[^a-zA-Z_]/, "_");
        return jsonToXml(value, safeKey, newIndent);
      })
      .join("\n");
    return `${indent}<${rootName}>\n${children}\n${indent}</${rootName}>`;
  }

  return `${indent}<${rootName}>${String(obj)}</${rootName}>`;
}

export default function JsonToXmlConverter() {
  const [jsonFile, setJsonFile] = useState<JsonFile | null>(null);
  const [jsonText, setJsonText] = useState<string>("");
  const [xmlOutput, setXmlOutput] = useState<string>("");
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [inputMode, setInputMode] = useState<"file" | "text">("file");

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileName = file.name.toLowerCase();
    const isValidExtension = fileName.endsWith(".json");

    if (file.type === "application/json" || isValidExtension) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setJsonFile({
          file,
          id: Math.random().toString(36).substr(2, 9),
          content,
        });
        setJsonText(content);
        setInputMode("file");
      };
      reader.readAsText(file);
      setError(null);
      setSuccess(false);
      setXmlOutput("");
    } else {
      setError(
        `${file.name} is not a supported format. Please use JSON files.`
      );
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setJsonFile(null);
    setJsonText("");
    setXmlOutput("");
    setError(null);
    setSuccess(false);
    const fileInput = document.getElementById(
      "json-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const convertToXml = async () => {
    const content = jsonText.trim();

    if (!content) {
      setError("Please provide JSON content to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      // Parse JSON to validate it
      const jsonObj = JSON.parse(content);

      // Convert to XML
      const xmlDeclaration = '<?xml version="1.0" encoding="UTF-8"?>\n';
      const xmlContent = jsonToXml(jsonObj, "root", "");
      const fullXml = xmlDeclaration + xmlContent;

      setXmlOutput(fullXml);
      setSuccess(true);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError("Invalid JSON format. Please check your JSON syntax.");
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to convert JSON to XML"
        );
      }
    } finally {
      setConverting(false);
    }
  };

  const downloadXml = () => {
    if (!xmlOutput) return;

    const blob = new Blob([xmlOutput], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName =
      jsonFile?.file.name.replace(".json", ".xml") || "converted.xml";
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (!xmlOutput) return;

    try {
      await navigator.clipboard.writeText(xmlOutput);
      setSuccess(true);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Mode Toggle */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          <Button
            variant={inputMode === "file" ? "contained" : "outlined"}
            onClick={() => setInputMode("file")}
            sx={{
              ...(inputMode === "file" && {
                background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              }),
            }}
          >
            Upload File
          </Button>
          <Button
            variant={inputMode === "text" ? "contained" : "outlined"}
            onClick={() => setInputMode("text")}
            sx={{
              ...(inputMode === "text" && {
                background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              }),
            }}
          >
            Paste JSON
          </Button>
        </Stack>

        {inputMode === "file" ? (
          <>
            {/* Upload Area */}
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: "3px dashed",
                borderColor: dragActive ? "primary.main" : "grey.300",
                borderRadius: 3,
                p: 4,
                textAlign: "center",
                bgcolor: dragActive ? "primary.50" : "grey.50",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "primary.50",
                },
              }}
              onClick={() =>
                document.getElementById("json-file-input")?.click()
              }
            >
              <input
                id="json-file-input"
                type="file"
                accept=".json,application/json"
                onChange={(e) => handleFileSelect(e.target.files)}
                style={{ display: "none" }}
              />

              <CloudUpload
                sx={{
                  fontSize: 80,
                  color: dragActive ? "primary.main" : "grey.400",
                  mb: 2,
                }}
              />

              <Typography
                variant="h6"
                component="p"
                gutterBottom
                fontWeight={600}
                color="text.primary"
              >
                {dragActive
                  ? "Drop your JSON file here"
                  : "Click to upload or drag and drop"}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Supported format: JSON
              </Typography>

              <Button
                variant="contained"
                startIcon={<DataObject sx={{ color: "#ffffff" }} />}
                sx={{
                  mt: 3,
                  color: "#ffffff",
                  backgroundColor: "#5558d3",
                  "&:hover": {
                    backgroundColor: "#4448c2",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById("json-file-input")?.click();
                }}
              >
                Select JSON File
              </Button>
            </Box>

            {/* Selected File Preview */}
            {jsonFile && (
              <Fade in={!!jsonFile}>
                <Box sx={{ mt: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" component="p" fontWeight={600}>
                      Selected File
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Delete />}
                      onClick={removeFile}
                      color="error"
                      variant="outlined"
                    >
                      Remove
                    </Button>
                  </Box>

                  <Zoom in>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        bgcolor: "grey.50",
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "#F7DF1E",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DataObject sx={{ fontSize: 32, color: "#000" }} />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {jsonFile.file.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(jsonFile.file.size)}
                        </Typography>
                      </Box>

                      <IconButton
                        onClick={removeFile}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.9)",
                          "&:hover": {
                            bgcolor: "error.main",
                            color: "white",
                          },
                        }}
                      >
                        <Close />
                      </IconButton>
                    </Paper>
                  </Zoom>
                </Box>
              </Fade>
            )}
          </>
        ) : (
          /* Text Input Mode */
          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder='Paste your JSON here...\n\nExample:\n{\n  "name": "John",\n  "age": 30\n}'
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setJsonFile(null);
              setError(null);
              setSuccess(false);
              setXmlOutput("");
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
              },
            }}
          />
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && !xmlOutput && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            Copied to clipboard!
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Code sx={{ color: "#ffffff" }} />}
            onClick={convertToXml}
            disabled={converting || (!jsonFile && !jsonText.trim())}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting ? "Converting..." : "Convert to XML"}
          </Button>

          {(jsonFile || jsonText.trim()) && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<DataObject />}
                label="JSON File"
                color="primary"
                variant="outlined"
              />
              {jsonFile && (
                <Chip
                  label={formatFileSize(jsonFile.file.size)}
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Box>

        {/* XML Output */}
        {xmlOutput && (
          <Fade in={!!xmlOutput}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  XML Output
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={copyToClipboard}
                    variant="outlined"
                  >
                    Copy
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={downloadXml}
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                    }}
                  >
                    Download
                  </Button>
                </Stack>
              </Box>

              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  bgcolor: "#1e1e1e",
                  borderRadius: 2,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    color: "#d4d4d4",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {xmlOutput}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
